import { PointOptionsObject, SeriesOptionsType } from 'highcharts'
import {
  AncestorSegmentsMap,
  buildAncestorSegmentsMap,
  getAncestorSegments,
  getCategoryTitleSegments,
  segmentsToHtml,
  SegmentType
} from '../print/CategoryTitleVisitor'
import { FormatterType } from '../utils'
import { ChartYAxis } from './types'
import { CustomReportCategory } from '../../../../../redux/context/customReports/typesCategory'
import { CustomReportChartSection } from '../../../../../redux/context/customReports/typesSection'
import { CustomReportVariable } from '../../../../../redux/context/customReports/typesVariable'
import { NumberFormatConfig } from '../../../../../shared/utils/numberScale'
import { VariableType } from '../../../../../components/Table/types'
import { getTitleByLanguage } from '../customReportTable/utils'
import { getLeafCategories } from './utils'
import { formatVariableValue, resolveFormat } from '../grid/utils/valueFormatting'

const CHART_SEGMENT_STYLES: Partial<Record<SegmentType, string>> = {
  period: 'font-size:11px;line-height:0.9',
  dataType: 'font-size:9px;line-height:0.9;opacity:0.6'
}

type CompanySegmentVisibility = {
  ancestorMap: AncestorSegmentsMap
  // Company name only earns its own segment when leaves span more than one company —
  // with a single company it's noise repeated on every axis label/series name.
  hideCompanySegment: boolean
}

const getCompanySegmentVisibility = (
  categories: CustomReportCategory[],
  leafs: CustomReportCategory[]
): CompanySegmentVisibility => {
  const ancestorMap = buildAncestorSegmentsMap(categories)
  const companyNames = new Set(
    leafs.flatMap(leaf =>
      getAncestorSegments(leaf, ancestorMap)
        .filter(s => s.type === 'company')
        .map(s => s.text)
    )
  )
  return { ancestorMap, hideCompanySegment: companyNames.size <= 1 }
}

const getLeafLabel = (
  leaf: CustomReportCategory,
  categories: CustomReportCategory[],
  { ancestorMap, hideCompanySegment }: CompanySegmentVisibility
): string => {
  const ancestorSegments = getAncestorSegments(leaf, ancestorMap).filter(
    s => !hideCompanySegment || s.type !== 'company'
  )
  const segments = [...ancestorSegments, ...getCategoryTitleSegments(leaf, categories)]
  return segmentsToHtml(segments, CHART_SEGMENT_STYLES)
}

export const getXAxisCategories = (section: CustomReportChartSection): string[] => {
  const leafs = getLeafCategories(section.categories)
  if (section.style?.switchRowsAndColumns) {
    return section.variables.map(v => getTitleByLanguage(v) || '')
  }
  const visibility = getCompanySegmentVisibility(section.categories, leafs)
  return leafs.map(l => getLeafLabel(l, section.categories, visibility))
}

const getRawValue = (variable: CustomReportVariable, category: CustomReportCategory): number | null => {
  const balance = variable.balances?.find(b => b.groupId === category.id)
  return typeof balance?.value === 'number' ? balance.value : null
}

export type FormattedPoint = PointOptionsObject & {
  custom?: { numberFormat: NumberFormatConfig; variableType?: VariableType }
}

const readFormat = (
  point: FormattedPoint | undefined
): { numberFormat: NumberFormatConfig; variableType?: VariableType } =>
  point?.custom ?? { numberFormat: { decimals: 0 } }

function dataLabelFormatter(this: FormatterType) {
  const { numberFormat, variableType } = readFormat(this.point.options as FormattedPoint | undefined)
  return formatVariableValue(this.point.y, numberFormat, variableType)
}

function tooltipPointFormatter(this: Highcharts.Point) {
  const { numberFormat, variableType } = readFormat(this.options as FormattedPoint)
  // Tooltip shows the full unscaled value — drop displayScale.
  const unscaled = { decimals: numberFormat.decimals ?? 0 }
  return `${this.series.name}: <b>${formatVariableValue(this.y, unscaled, variableType)}</b><br/>`
}

const seriesOptions = (
  item: CustomReportVariable | CustomReportCategory,
  name: string,
  data: FormattedPoint[]
): Highcharts.SeriesOptionsType =>
  ({
    ...item.style,
    name,
    data,
    yAxis: item?.style?.yAxis ?? ChartYAxis.left,
    dataLabels: {
      formatter: dataLabelFormatter,
      y: -2,
      style: { fontSize: '10px', textOutline: 'none', color: '#333333' }
    },
    tooltip: { pointFormatter: tooltipPointFormatter },
    style: {
      fontWeight: (item as CustomReportVariable).style?.fontWeight ? 'bold' : undefined,
      fontStyle: (item as CustomReportVariable).style?.fontStyle
    }
  } as unknown as Highcharts.SeriesOptionsType)

export const getSeries = (sec: CustomReportChartSection): Highcharts.SeriesOptionsType[] => {
  const { categories, variables } = sec
  const leafs = getLeafCategories(categories)

  // Y values are raw balance values (e.g. 0.25 for 25 %). Formatting honors
  // variableType via Intl's percent style, matching the grid's convention.
  if (!sec?.style?.switchRowsAndColumns) {
    return variables.map(v => {
      const data: FormattedPoint[] = leafs.map(cat => ({
        y: getRawValue(v, cat),
        custom: resolveFormat(v, cat)
      }))
      return seriesOptions(v, getTitleByLanguage(v) || '', data)
    })
  }

  // Switched: one series per leaf category; each point iterates variables, so
  // per-point format remains accurate even when variables have different formats.
  const visibility = getCompanySegmentVisibility(categories, leafs)
  return leafs.map(cat => {
    const data: FormattedPoint[] = variables.map(v => ({
      y: getRawValue(v, cat),
      custom: resolveFormat(v, cat)
    }))
    return seriesOptions(cat, getLeafLabel(cat, categories, visibility), data)
  })
}

export type DrillData = { [key: string]: Highcharts.PointOptionsObject[] }

export const getMaxSectionSeriesValue = (
  series: Highcharts.SeriesOptionsType[],
  yAxis: ChartYAxis = ChartYAxis.left
): number | undefined => {
  const values = series
    .filter(s => (((s as any).yAxis as ChartYAxis | undefined) ?? ChartYAxis.left) === yAxis)
    .flatMap(s => ((s as any).data as FormattedPoint[] | undefined) ?? [])
    .map(p => p.y)
    .filter((n): n is number => typeof n === 'number' && !Number.isNaN(n))

  if (values.length === 0) return undefined
  return Math.abs(Math.max(...values) * 1.3)
}

export const labelFormatter = (options: (SeriesOptionsType | PointOptionsObject) & { style?: any }, name: string) => {
  const style = options?.style
  const inlineStyle = style
    ? Object.entries(style)
        .map(([key, value]) => `${key}: ${value}`)
        .join('; ')
    : ''
  return `<div style="${inlineStyle}">${name}</div>`
}
